// ** React Imports
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'

// ** MUI Components
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Snackbar from '@mui/material/Snackbar'
import CircularProgress from '@mui/material/CircularProgress'

// ** APIs
import { getOrderForm, getOrderFormDocumentContent, postOrderForm, sendOrderFormToLumin } from 'configs/axios/api_helper'

// ── Field definitions ──
const FIELD_SECTIONS = [
  {
    label: 'Customer Information',
    fields: [
      { key: 'customer_name', label: 'Legal Company Name' },
      { key: 'customer_domain', label: 'Customer Domain' },
      { key: 'customer_phone', label: 'Customer Phone', type: 'phone' },
      { key: 'full_customer_address', label: 'Customer Full Address', type: 'address' }
    ]
  },
  {
    label: 'Authorized Signer',
    fields: [
      { key: 'customer_signer_first_name', label: 'Signer First Name' },
      { key: 'customer_signer_last_name', label: 'Signer Last Name' },
      { key: 'customer_signer_title', label: 'Signer Title' },
      { key: 'customer_signer_email', label: 'Signer Email' }
    ]
  },
  {
    label: 'Day-to-Day Contact',
    fields: [
      { key: 'contact_first_name', label: 'Contact First Name' },
      { key: 'contact_last_name', label: 'Contact Last Name' },
      { key: 'contact_title', label: 'Contact Title' },
      { key: 'contact_phone', label: 'Contact Phone', type: 'phone' },
      { key: 'contact_email', label: 'Contact Email' }
    ]
  }
]

const ALL_FIELDS = FIELD_SECTIONS.flatMap(s => s.fields)
const TOTAL_FIELDS = ALL_FIELDS.length

// ── Color tokens ──
const PC_NAVY = '#1E66AB'
const PC_SKY = '#38B4E7'
const PC_GREEN = '#34A853'
const PC_RED = '#EA4234'
const PC_YELLOW = '#FBC321'
const PC_GOOGLE_BLUE = '#4285F4'

// ── SVG Icons ──
const CheckIcon = () => (
  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
    <path
      d='M2.5 7.5L5.5 10.5L11.5 3.5'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

const PenIcon = () => (
  <svg width='11' height='11' viewBox='0 0 11 11' fill='none'>
    <path
      d='M1 10L3.5 9.5L9.5 3.5L7.5 1.5L1.5 7.5L1 10Z'
      stroke='currentColor'
      strokeWidth='1.2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

// ── Google Maps JS API loader (uses Maps JavaScript API — no separate Places API needed) ──
function useGoogleMapsScript() {
  const [loaded, setLoaded] = useState(!!window.google?.maps?.places)

  useEffect(() => {
    if (window.google?.maps?.places) { setLoaded(true); return }

    const existing = document.getElementById('google-maps-script')
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true))
      return
    }

    const script = document.createElement('script')
    script.id = 'google-maps-script'
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&v=weekly`
    script.async = true
    script.onload = () => setLoaded(true)
    document.head.appendChild(script)
  }, [])

  return loaded
}

function parseAddressComponents(components) {
  const get = type => {
    const comp = components.find(c => c.types.includes(type))
    return comp ? (comp.longText ?? comp.long_name ?? null) : null
  }
  const streetNumber = get('street_number')
  const route = get('route')
  return {
    street: [streetNumber, route].filter(Boolean).join(' ') || null,
    city: get('locality') || get('administrative_area_level_2'),
    state: get('administrative_area_level_1'),
    zip: get('postal_code'),
    country: get('country')
  }
}

// ── Shared card styles ──
function getCardSx(isConfirmed, isActive) {
  if (isConfirmed)
    return {
      opacity: 1,
      border: `1.5px solid ${PC_GREEN}`,
      bgcolor: '#f0fdf4',
      borderRadius: '10px',
      p: '16px 20px',
      mb: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      '&:hover': { borderColor: '#22c55e' },
      '&:hover .edit-link': { opacity: 1 }
    }
  if (isActive)
    return {
      opacity: 1,
      border: `1.5px solid ${PC_SKY}`,
      boxShadow: '0 0 0 3px rgba(56,180,231,0.15)',
      bgcolor: '#ffffff',
      borderRadius: '10px',
      p: '16px 20px',
      mb: '10px',
      cursor: 'default',
      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      animation: 'slideIn 0.25s ease-out'
    }
  return {
    opacity: 0.5,
    border: '1.5px solid #e2e8f0',
    bgcolor: '#f9fafb',
    borderRadius: '10px',
    p: '16px 20px',
    mb: '10px',
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    '&:hover': { opacity: 0.7, borderColor: '#cbd5e1' }
  }
}

function getIconSx(isConfirmed, isActive) {
  if (isConfirmed) return { bgcolor: PC_GREEN, border: `1.5px solid ${PC_GREEN}`, color: 'white', animation: 'checkPop 0.3s ease-out' }
  if (isActive) return { bgcolor: PC_SKY, border: `1.5px solid ${PC_SKY}`, color: 'white' }
  return { bgcolor: '#f1f5f9', border: '1.5px solid #cbd5e1', color: '#94a3b8' }
}

// ── CardShell — shared visual wrapper ──
function CardShell({ field, value, isActive, isConfirmed, onActivate, onEdit, children }) {
  const cardRef = useRef(null)
  const fieldNumber = ALL_FIELDS.findIndex(f => f.key === field.key) + 1

  useEffect(() => {
    if (isActive) {
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100)
    }
  }, [isActive])

  return (
    <Box
      ref={cardRef}
      sx={getCardSx(isConfirmed, isActive)}
      onClick={() => {
        if (!isActive && !isConfirmed) onActivate()
        if (isConfirmed) onEdit()
      }}
    >
      {/* Top row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '4px' }}>
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.3s ease',
            ...getIconSx(isConfirmed, isActive)
          }}
        >
          {isConfirmed ? <CheckIcon /> : isActive ? <PenIcon /> : (
            <Typography sx={{ fontSize: '10px', lineHeight: 1 }}>{fieldNumber}</Typography>
          )}
        </Box>

        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            flex: 1,
            color: isConfirmed ? PC_GREEN : isActive ? PC_NAVY : '#64748b'
          }}
        >
          {field.label}
        </Typography>

        {isConfirmed && (
          <Typography
            className='edit-link'
            component='button'
            onClick={e => { e.stopPropagation(); onEdit() }}
            sx={{
              fontSize: '12px',
              color: PC_SKY,
              fontWeight: 500,
              cursor: 'pointer',
              opacity: 0,
              transition: 'opacity 0.2s',
              background: 'none',
              border: 'none',
              fontFamily: 'inherit',
              p: 0
            }}
          >
            Edit
          </Typography>
        )}
      </Box>

      {/* Body */}
      {isActive ? children : (
        <Typography
          sx={{
            fontSize: '15px',
            color: value ? '#1a1a2e' : '#cbd5e1',
            fontStyle: value ? 'normal' : 'italic',
            fontWeight: isConfirmed ? 500 : 400,
            pl: '34px',
            minHeight: '20px',
            lineHeight: 1.4
          }}
        >
          {value || 'Not provided'}
        </Typography>
      )}
    </Box>
  )
}

// ── ConfirmButton ──
function ConfirmButton({ onClick }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pl: '34px', mt: '12px' }}>
      <Button
        onClick={e => { e.stopPropagation(); onClick() }}
        sx={{
          bgcolor: PC_NAVY,
          color: 'white',
          borderRadius: '8px',
          fontSize: '13px',
          fontWeight: 600,
          px: '20px',
          py: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          '&:hover': { bgcolor: '#174f8a', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(30,102,171,0.3)' }
        }}
      >
        Confirm <CheckIcon />
      </Button>
    </Box>
  )
}

// ── FieldCard Component ──
function FieldCard({ field, value, editValue, isActive, isConfirmed, onActivate, onConfirm, onEdit, onChange }) {
  const inputRef = useRef(null)
  const [showError, setShowError] = useState('')

  useEffect(() => {
    if (isActive && inputRef.current) inputRef.current.focus()
  }, [isActive])

  useEffect(() => {
    if (showError && editValue.trim()) setShowError('')
  }, [editValue, showError])

  const handleConfirm = () => {
    if (!editValue.trim()) { setShowError('This field is required'); return }
    if (field.type === 'phone' && !isValidPhone(editValue)) { setShowError('Please enter a valid phone number'); return }
    setShowError('')
    onConfirm()
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') { e.preventDefault(); handleConfirm() }
  }

  return (
    <CardShell field={field} value={value} isActive={isActive} isConfirmed={isConfirmed} onActivate={onActivate} onEdit={onEdit}>
      <Box sx={{ pl: '34px', pt: '4px' }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          size='small'
          value={editValue}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          error={!!showError}
          helperText={showError}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontSize: '15px',
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: PC_SKY },
              '&.Mui-focused fieldset': { borderColor: PC_SKY, borderWidth: '1.5px' },
              '&.Mui-error fieldset': { borderColor: PC_RED }
            }
          }}
        />
      </Box>
      <ConfirmButton onClick={handleConfirm} />
    </CardShell>
  )
}

// ── AddressFieldCard Component ──
function AddressFieldCard({ field, value, editValue, isActive, isConfirmed, onActivate, onConfirm, onEdit, onAddressChange, mapsLoaded }) {
  const containerRef = useRef(null)
  const elementRef = useRef(null)
  const [isChanging, setIsChanging] = useState(!value)
  const [showError, setShowError] = useState(false)

  const addressSelected = !!editValue && editValue !== value

  // Reset when field is deactivated
  useEffect(() => {
    if (!isActive) {
      setIsChanging(!value)
      setShowError(false)
      if (elementRef.current && containerRef.current) {
        containerRef.current.removeChild(elementRef.current)
        elementRef.current = null
      }
    }
  }, [isActive, value])

  // Mount PlaceAutocompleteElement when in "changing" mode
  useEffect(() => {
    if (!isActive || !isChanging || !mapsLoaded || !containerRef.current || elementRef.current) return

    const el = new window.google.maps.places.PlaceAutocompleteElement()
    el.style.cssText = 'width: 100%; display: block;'
    console.log('[Address] PlaceAutocompleteElement created:', el)

    el.addEventListener('gmp-select', async ({ placePrediction }) => {
      console.log('[Address] gmp-select fired, placePrediction:', placePrediction)
      const place = placePrediction.toPlace()
      try {
        await place.fetchFields({ fields: ['formattedAddress', 'addressComponents', 'location'] })
        console.log('[Address] fetchFields done — formattedAddress:', place.formattedAddress, 'addressComponents:', place.addressComponents)
      } catch (e) {
        console.warn('[Address] fetchFields error:', e)
      }

      const parsed = parseAddressComponents(place.addressComponents || [])
      console.log('[Address] parsed components:', parsed)

      if (!parsed.zip && place.location) {
        try {
          const geocoder = new window.google.maps.Geocoder()
          const { results } = await geocoder.geocode({ location: place.location })
          const zipComp = results?.[0]?.address_components?.find(c => c.types.includes('postal_code'))
          if (zipComp) parsed.zip = zipComp.long_name
          console.log('[Address] geocoder zip:', parsed.zip)
        } catch (e) {
          console.warn('[Address] geocoder error:', e)
        }
      }

      onAddressChange({ formatted_address: place.formattedAddress, ...parsed })
      setShowError(false)
    })

    containerRef.current.appendChild(el)
    elementRef.current = el

    return () => {
      console.log('[Address] effect cleanup')
      if (el.parentNode === containerRef.current) containerRef.current.removeChild(el)
      elementRef.current = null
    }
  }, [isActive, isChanging, mapsLoaded, onAddressChange])

  const handleConfirm = () => {
    console.log('[Address] handleConfirm — isChanging:', isChanging, 'value:', value, 'editValue:', editValue, 'addressSelected:', addressSelected)
    if (!isChanging && value) {
      onConfirm()
      return
    }
    if (!addressSelected) { setShowError(true); return }
    setShowError(false)
    onConfirm()
  }

  return (
    <CardShell field={field} value={value} isActive={isActive} isConfirmed={isConfirmed} onActivate={onActivate} onEdit={onEdit}>
      <Box sx={{ pl: '34px', pt: '4px' }}>
        {!isChanging && value ? (
          // Show existing backend value with option to change
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '12px',
              py: '8px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              bgcolor: '#f9fafb'
            }}
          >
            <Typography sx={{ fontSize: '14px', color: '#1a1a2e', flex: 1, lineHeight: 1.4 }}>{value}</Typography>
            <Button
              onClick={() => setIsChanging(true)}
              sx={{
                fontSize: '12px',
                color: PC_RED,
                fontWeight: 600,
                minWidth: 'auto',
                ml: 1,
                p: '2px 8px',
                '&:hover': { bgcolor: '#fff5f5' }
              }}
            >
              Change
            </Button>
          </Box>
        ) : (
          <>
            <Box ref={containerRef} />
            {showError && (
              <Typography sx={{ fontSize: '12px', color: PC_RED, mt: 0.5, ml: 0.25 }}>
                Please select an address from the suggestions
              </Typography>
            )}
          </>
        )}
      </Box>
      <ConfirmButton onClick={handleConfirm} />
    </CardShell>
  )
}

// ── FormHeader Component ──
function FormHeader({ confirmedCount, totalCount }) {
  const progressPct = (confirmedCount / totalCount) * 100

  return (
    <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 }}>
      {/* Color bar */}
      <Box sx={{ height: '4px', display: 'flex' }}>
        <Box sx={{ flex: 1, bgcolor: PC_RED }} />
        <Box sx={{ flex: 3, bgcolor: PC_GOOGLE_BLUE }} />
        <Box sx={{ flex: 1, bgcolor: PC_GREEN }} />
        <Box sx={{ flex: 1, bgcolor: PC_YELLOW }} />
      </Box>
      {/* Header inner */}
      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        {/* Logo area */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            component='img'
            src='https://premiercloud.com/wp-content/uploads/2022/09/Premier_Cloud_Brandmark.svg'
            alt='Premier Cloud'
            sx={{ height: 36 }}
          />
          <Box sx={{ width: '1px', height: 28, bgcolor: '#e2e8f0' }} />
          <Typography sx={{ fontSize: '15px', fontWeight: 500, color: '#64748b' }}>Order Form Review</Typography>
        </Box>
        {/* Progress area */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: PC_NAVY, whiteSpace: 'nowrap' }}>
            {confirmedCount}/{totalCount}
          </Typography>
          <Box sx={{ width: 120, height: 6, bgcolor: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
            <Box
              sx={{
                height: '100%',
                width: `${progressPct}%`,
                background: `linear-gradient(90deg, ${PC_GREEN}, #2dd470)`,
                borderRadius: '3px',
                transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)'
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

// ── Helpers ──
function isValidPhone(value) {
  return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{3,4}[-\s.]?[0-9]{3,4}$/.test(value.trim())
}

function splitName(fullName) {
  if (!fullName) return { first: '', last: '' }
  const parts = fullName.trim().split(' ')
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' }
}

function buildAddressDisplay(data) {
  return [data.address_street, data.address_city, data.address_state, data.address_zip, data.address_country]
    .filter(Boolean)
    .join(', ')
}

// ── Main Forms Component ──
const Forms = () => {
  const { id } = useParams()
  const mapsLoaded = useGoogleMapsScript()

  // ** States
  const [isLoading, setIsLoading] = useState(true)
  const [formLoading, setFormLoading] = useState(false)
  const [error, setError] = useState(null)
  const [isDone, setIsDone] = useState(false)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [documentUrl, setDocumentUrl] = useState(null)
  const [docKey, setDocKey] = useState(0)
  const [documentError, setDocumentError] = useState(null)

  // Field state
  const [values, setValues] = useState({})
  const [editValues, setEditValues] = useState({})
  const [confirmed, setConfirmed] = useState(new Set())
  const [activeField, setActiveField] = useState(null)
  const [formId, setFormId] = useState(null)
  const [savedValues, setSavedValues] = useState({})
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)

  // ** Load form data on mount
  useEffect(() => {
    const loadFormData = async () => {
      try {
        setIsLoading(true)
        const { data } = await getOrderForm(id)
        setFormId(data.id)

        const signerName = splitName(data.customer_signer_name)
        const contactName = splitName(data.contact_name)

        const initialValues = {
          customer_name: data.customer_name || '',
          customer_domain: data.customer_domain || '',
          customer_phone: data.customer_phone || '',
          full_customer_address: buildAddressDisplay(data) || data.full_customer_address || '',
          address_street: data.address_street || null,
          address_city: data.address_city || null,
          address_state: data.address_state || null,
          address_zip: data.address_zip || null,
          address_country: data.address_country || null,
          customer_signer_first_name: data.customer_signer_first_name || signerName.first,
          customer_signer_last_name: data.customer_signer_last_name || signerName.last,
          customer_signer_title: data.customer_signer_title || '',
          customer_signer_email: data.customer_signer_email || '',
          contact_first_name: data.contact_first_name || contactName.first,
          contact_last_name: data.contact_last_name || contactName.last,
          contact_title: data.contact_title || '',
          contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || ''
        }

        setValues(initialValues)
        setSavedValues(initialValues)
        setActiveField(ALL_FIELDS[0].key)
        setIsDone(data?.status === 'done')

        await loadDocumentContent()
      } catch (error) {
        setError(error)
      } finally {
        setIsLoading(false)
      }
    }

    const loadDocumentContent = async () => {
      try {
        const response = await getOrderFormDocumentContent(id)
        const blobUrl = URL.createObjectURL(response.data)
        setDocumentUrl(prev => {
          if (prev) URL.revokeObjectURL(prev)
          return blobUrl
        })
      } catch (error) {
        console.error('Error loading document:', error)
        setDocumentError(error)
      }
    }

    if (id) {
      loadFormData()
    }
  }, [id])

  // ** Field interaction handlers
  const activateField = useCallback(
    key => {
      setActiveField(key)
      setEditValues(prev => ({ ...prev, [key]: values[key] || '' }))
    },
    [values]
  )

  const confirmField = useCallback(
    key => {
      const newValue = editValues[key] ?? values[key]
      setValues(prev => {
        const updated = { ...prev, [key]: newValue }
        const changed = ALL_FIELDS.some(f => (updated[f.key] || '') !== (savedValues[f.key] || ''))
        setHasUnsavedChanges(changed)
        return updated
      })
      setConfirmed(prev => new Set([...prev, key]))

      // Find next unconfirmed field
      const currentIdx = ALL_FIELDS.findIndex(f => f.key === key)
      let nextField = null
      for (let i = 1; i <= ALL_FIELDS.length; i++) {
        const candidate = ALL_FIELDS[(currentIdx + i) % ALL_FIELDS.length]
        if (!confirmed.has(candidate.key) && candidate.key !== key) {
          nextField = candidate.key
          break
        }
      }
      if (nextField) {
        setActiveField(nextField)
        setEditValues(prev => ({ ...prev, [nextField]: values[nextField] || '' }))
      } else {
        setActiveField(null)
      }
    },
    [editValues, confirmed, values, savedValues]
  )

  const editField = useCallback(
    key => {
      setConfirmed(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
      setActiveField(key)
      setEditValues(prev => ({ ...prev, [key]: values[key] || '' }))
    },
    [values]
  )

  // Called when a place is selected in the address field
  const handleAddressChange = useCallback(({ formatted_address, street, city, state, zip, country }) => {
    // editValues drives the value applied on confirmField
    setEditValues(prev => ({ ...prev, full_customer_address: formatted_address }))
    // Store address components immediately so they're ready for the update payload
    setValues(prev => ({ ...prev, address_street: street, address_city: city, address_state: state, address_zip: zip, address_country: country }))
  }, [])

  const handleUpdate = async () => {
    try {
      setUpdateLoading(true)
      await postOrderForm(formId, {
        is_customer_update: true,
        customer_name: values.customer_name,
        customer_domain: values.customer_domain,
        customer_phone: values.customer_phone,
        full_customer_address: values.full_customer_address,
        customer_signer_first_name: values.customer_signer_first_name,
        customer_signer_last_name: values.customer_signer_last_name,
        customer_signer_title: values.customer_signer_title,
        customer_signer_email: values.customer_signer_email,
        contact_first_name: values.contact_first_name,
        contact_last_name: values.contact_last_name,
        contact_title: values.contact_title,
        contact_phone: values.contact_phone,
        contact_email: values.contact_email,
        address_street: values.address_street,
        address_city: values.address_city,
        address_state: values.address_state,
        address_zip: values.address_zip,
        address_country: values.address_country
      })
      setSavedValues(values)
      setHasUnsavedChanges(false)
      // Refresh the document preview after update
      try {
        const response = await getOrderFormDocumentContent(id)
        const blobUrl = URL.createObjectURL(response.data)
        setDocumentUrl(prev => {
          if (prev) URL.revokeObjectURL(prev)
          return blobUrl
        })
        setDocKey(prev => prev + 1)
      } catch (e) {
        console.error('Error refreshing document:', e)
      }
    } catch (error) {
      if (error?.response?.status === 401) setMessage('This Order is already sent.')
      else setMessage('An error has occurred please try again.')
      setOpen(true)
    } finally {
      setUpdateLoading(false)
    }
  }

  const handleFinalSubmit = async () => {
    try {
      setFormLoading(true)
      await sendOrderFormToLumin(formId)
      setIsDone(true)
    } catch (error) {
      if (error?.response?.status === 401) setMessage('This Order is already sent.')
      else setMessage('An error has occurred please try again.')
      setOpen(true)
    } finally {
      setFormLoading(false)
    }
  }

  // ── Early returns ──

  if (isLoading)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )

  if (error)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant='h5' sx={{ fontWeight: 600, color: 'error.main' }}>
          Unable to load form. Please check the link and try again.
        </Typography>
      </Box>
    )

  if (documentError)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography variant='h5' sx={{ fontWeight: 600, color: 'error.main' }}>
          You don't have access to this page
        </Typography>
      </Box>
    )

  if (isDone)
    return (
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
        <FormHeader confirmedCount={TOTAL_FIELDS} totalCount={TOTAL_FIELDS} />
        <Box sx={{ maxWidth: 720, mx: 'auto', px: 3, mt: 6 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 2,
              minHeight: '60vh',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                bgcolor: '#f0fdf4',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1
              }}
            >
              <Typography sx={{ fontSize: '36px', color: PC_GREEN, lineHeight: 1 }}>✓</Typography>
            </Box>
            <Typography variant='h4' sx={{ fontWeight: 700, color: PC_NAVY }}>
              All Set!
            </Typography>
            <Typography variant='body1' sx={{ color: '#64748b', maxWidth: 400, lineHeight: 1.5 }}>
              Your order form has been validated. You'll receive your document for signature shortly
              {values.customer_signer_email && (
                <>
                  {' '}
                  at <strong>{values.customer_signer_email}</strong>
                </>
              )}
              .
            </Typography>
          </Box>
        </Box>
      </Box>
    )

  // ── Main render ──
  const allConfirmed = confirmed.size === TOTAL_FIELDS

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes checkPop {
          0%   { transform: scale(0.5); }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
      `}</style>

      <FormHeader confirmedCount={confirmed.size} totalCount={TOTAL_FIELDS} />

          <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 60px)' }}>
            {/* Left side — Form */}
            <Box
              sx={{
                width: '33.333%',
                overflowY: 'auto',
                px: 3,
                pt: 4,
                pb: '120px'
              }}
            >
              {/* Welcome text */}
              <Box sx={{ textAlign: 'center', mb: 4.5, maxWidth: 600, mx: 'auto' }}>
                <Typography variant='h5' sx={{ fontWeight: 700, color: PC_NAVY, mb: 1 }}>
                  Please Review Your Order Details
                </Typography>
                <Typography variant='body2' sx={{ color: '#64748b', lineHeight: 1.5 }}>
                  Confirm each field below is correct. Click a field to review it, then press <strong>Confirm</strong> to
                  proceed to the next one.
                </Typography>
              </Box>

              {/* Field sections */}
              <Box sx={{ maxWidth: 600, mx: 'auto' }}>
                {FIELD_SECTIONS.map(section => (
                  <React.Fragment key={section.label}>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '1.2px',
                        color: '#64748b',
                        mt: 3.5,
                        mb: 1.5,
                        pl: 0.5
                      }}
                    >
                      {section.label}
                    </Typography>
                    {section.fields.map(field =>
                      field.type === 'address' ? (
                        <AddressFieldCard
                          key={field.key}
                          field={field}
                          value={values[field.key]}
                          editValue={editValues[field.key] ?? ''}
                          isActive={activeField === field.key}
                          isConfirmed={confirmed.has(field.key)}
                          onActivate={() => activateField(field.key)}
                          onConfirm={() => confirmField(field.key)}
                          onEdit={() => editField(field.key)}
                          onAddressChange={handleAddressChange}
                          mapsLoaded={mapsLoaded}
                        />
                      ) : (
                        <FieldCard
                          key={field.key}
                          field={field}
                          value={values[field.key]}
                          editValue={editValues[field.key] ?? values[field.key] ?? ''}
                          isActive={activeField === field.key}
                          isConfirmed={confirmed.has(field.key)}
                          onActivate={() => activateField(field.key)}
                          onConfirm={() => confirmField(field.key)}
                          onEdit={() => editField(field.key)}
                          onChange={val => setEditValues(prev => ({ ...prev, [field.key]: val }))}
                        />
                      )
                    )}
                  </React.Fragment>
                ))}
              </Box>
            </Box>

            {/* Right side — Google Doc Preview */}
            <Box
              sx={{
                width: '66.667%',
                borderLeft: '1px solid #e2e8f0',
                position: 'sticky',
                top: 60,
                height: 'calc(100vh - 60px)',
                bgcolor: '#ffffff'
              }}
            >
              {documentUrl ? (
                <Box sx={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                  <iframe
                    key={docKey}
                    src={`${documentUrl}#navpanes=0`}
                    title='Order Form Document Preview'
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '-8%',
                      width: '116%',
                      height: '100%',
                      border: 'none'
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#94a3b8'
                  }}
                >
                  <Typography>No document preview available</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Fixed bottom submit bar */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '33.333%',
              bgcolor: 'white',
              borderTop: '1px solid #e2e8f0',
              py: 2,
              px: 3,
              zIndex: 100
            }}
          >
            <Box
              sx={{
                maxWidth: 600,
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
                {allConfirmed ? (
                  <>
                    <strong style={{ color: PC_GREEN }}>All fields confirmed</strong> — ready to send
                  </>
                ) : (
                  <>
                    {confirmed.size} of {TOTAL_FIELDS} fields confirmed
                  </>
                )}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                {hasUnsavedChanges && (
                  <Button
                    disabled={!allConfirmed || updateLoading}
                    onClick={handleUpdate}
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: 700,
                      color: 'white',
                      bgcolor: allConfirmed ? PC_SKY : undefined,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      '&:hover': allConfirmed
                        ? { bgcolor: '#2da3d6', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(56,180,231,0.3)' }
                        : {},
                      '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                    }}
                  >
                    {updateLoading && <CircularProgress size='1rem' sx={{ color: 'white' }} />}
                    {updateLoading ? 'Updating...' : 'Update'}
                  </Button>
                )}

                <Button
                  disabled={!allConfirmed || formLoading || hasUnsavedChanges}
                  onClick={handleFinalSubmit}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: 'white',
                    background: allConfirmed && !hasUnsavedChanges ? `linear-gradient(135deg, ${PC_NAVY}, ${PC_SKY})` : undefined,
                    boxShadow: allConfirmed && !hasUnsavedChanges ? '0 4px 14px rgba(30,102,171,0.3)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': allConfirmed && !hasUnsavedChanges
                      ? { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(30,102,171,0.4)', background: `linear-gradient(135deg, ${PC_NAVY}, ${PC_SKY})` }
                      : {},
                    '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                  }}
                >
                  {formLoading && <CircularProgress size='1rem' sx={{ color: 'white' }} />}
                  {formLoading ? 'Sending...' : 'Send for Signature'}
                </Button>
              </Box>
            </Box>
          </Box>
      <Snackbar
        open={open}
        onClose={() => setOpen(false)}
        message={message}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Box>
  )
}

export default Forms
